// S11 — the create-tenant wizard (spec 15 §4, §3.2 "Create (expanded)").
//
// Four steps: name and subdomain, industry pack and data region, defaults
// (language, currency, time zone), then the admin's email with a review and
// Create. After Create the screen follows the tenant through setup by polling
// its detail, and ends on the live link or on the failure with Retry.
//
// The subdomain is checked as it is typed (GET subdomains/:s/availability),
// after the pattern check the browser can do alone. The check is advice: the
// create decides under its own locks, so a 409 on submit sends the operator
// back to step 1 with the reason.
//
// A create whose answer is lost may still have made the tenant. The retry
// sends the same Idempotency-Key while the draft is unchanged, so the server
// replays the first answer (lib/held-key.ts). If the draft did change and the
// retry is told the subdomain is taken, the wizard looks the subdomain up and,
// when the tenant there is the one an earlier try sent, follows it instead of
// asking for another name.
//
// Step 2 also picks the plan (spec 15 §2.7, M15-6): the offered plans are the
// ones the server would accept for this creator and pack, live and carrying
// the pack, with sandbox plans only for a sandbox creator (D-477, D-615). The
// pilot plan is preselected when it is offered. A refusal on submit that names
// the plan sends the operator back to step 2.
//
// NOT HERE, on purpose: "Fill with demo data" (the showcase seed profile is
// track D's and has no API yet). Spec 15 §4 S11 records it.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PILOT_PLAN_CODE } from "@assessly/contracts";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { Field } from "../../../components/field";
import { Select } from "../../../components/select";
import { CardSkeleton, ErrorState, ForbiddenState } from "../../../components/states";
import { Stepper, type StepperStep } from "../../../components/stepper";
import { Banner } from "../../../components/toast";
import { LOCALE_ENDONYMS } from "../../../lib/locale";
import { useDebouncedValue } from "../../../lib/use-debounced-value";
import { Fact, Facts } from "../components/facts";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import {
  checkSubdomain,
  createTenant,
  getTenant,
  listTenants,
  listVerticalPacks,
  PlatformError,
  retryProvisioning,
  type CreateTenantInput,
  type PlatformMe,
  type PlatformRegion,
  type TenantDetail,
} from "../lib/platform-api";
import { isDefiniteAnswer, useHeldKey, withHeldKey } from "../lib/held-key";
import { listPlans } from "../lib/plans-api";
import { assignablePlans, formatMinor } from "../lib/plans";
import { PLANS_QUERY } from "./plans";
import { packLabel, pickLocalized } from "../lib/labels";
import {
  actionFailure,
  canCreateTenant,
  isOwnCreate,
  isProvisioningSettled,
  normalizeSubdomain,
  PLATFORM_REGIONS,
  SUBDOMAIN_PATTERN,
  TENANT_LOCALES,
  tenantCurrencies,
  tenantOrigin,
  type CreateAttempt,
  type TenantLocale,
} from "../lib/tenants";
import { actionMessage, ProvisioningSteps, TENANT_POLL_MS, tenantQueryKey } from "./tenant-detail";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const STEP_COUNT = 4;

/** A suggested subdomain from a company name: ASCII letters and digits only,
 * hyphen-joined, trimmed to the 63-character limit. Cyrillic or CJK names give
 * an empty suggestion, which leaves the field for the operator. */
export function suggestSubdomain(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 63)
    .replace(/-+$/u, "");
}

/** IANA time zones the browser knows, with the browser's own zone first. */
function timeZones(): readonly string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.("timeZone");
  const list = supported !== undefined && supported.length > 0 ? supported : ["UTC"];
  return list.includes("UTC") ? list : ["UTC", ...list];
}

function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export type SubdomainState =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "reserved" }
  | { kind: "taken" }
  | { kind: "unknown" };

interface Draft {
  name: string;
  subdomain: string;
  subdomainEdited: boolean;
  pack: string;
  planId: string;
  region: PlatformRegion;
  locale: TenantLocale;
  currency: string;
  timezone: string;
  adminEmail: string;
}

export function TenantCreateScreen({ me }: { me: PlatformMe }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!canCreateTenant(me.role)) {
    return (
      <section className="admin-screen" data-testid="admin-tenant-create-forbidden">
        <PageHeader
          title={t("admin.create.title")}
          crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        />
        <ForbiddenState
          title={t("admin.create.forbiddenTitle")}
          body={t("admin.create.forbiddenBody")}
        />
        <div className="admin-actions">
          <Button
            onClick={() => {
              void navigate({ to: "/console/tenants" });
            }}
          >
            {t("admin.tenant.back")}
          </Button>
        </div>
      </section>
    );
  }
  return <CreateWizard me={me} />;
}

function CreateWizard({ me }: { me: PlatformMe }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const zones = useMemo(timeZones, []);
  const currencies = useMemo(tenantCurrencies, []);
  const heldKey = useHeldKey();
  // The bodies of creates whose answer never arrived: one of them may have
  // made a tenant (see the header).
  const unclear = useRef<CreateAttempt[]>([]);

  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(() => ({
    name: "",
    subdomain: "",
    subdomainEdited: false,
    pack: "",
    planId: "",
    region: "ap-southeast-1",
    locale: "en",
    currency: "USD",
    timezone: browserZone(),
    adminEmail: "",
  }));
  const [created, setCreated] = useState<TenantDetail | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function update(patch: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  const packs = useQuery({
    queryKey: ["platform", "vertical-packs"],
    queryFn: ({ signal }) => listVerticalPacks(signal),
    staleTime: 5 * 60_000,
  });
  // The first pack is chosen for the operator once the list arrives.
  const firstPack = packs.data?.[0]?.key;
  useEffect(() => {
    if (draft.pack === "" && firstPack !== undefined) update({ pack: firstPack });
  }, [draft.pack, firstPack]);

  // The plans this creator may assign to a tenant on the chosen pack.
  const plans = useQuery({
    queryKey: [...PLANS_QUERY, { showRetired: false }],
    queryFn: ({ signal }) => listPlans(false, signal),
    staleTime: 60_000,
  });
  useSignInAgainOn401(plans.error);
  const offered = useMemo(
    () =>
      plans.data === undefined
        ? []
        : assignablePlans(plans.data, {
            sandboxTenant: me.scope === "sandbox",
            pack: draft.pack === "" ? null : draft.pack,
          }),
    [plans.data, me.scope, draft.pack],
  );
  // Keep the choice valid: an unoffered plan falls back to pilot, else the first.
  useEffect(() => {
    if (plans.data === undefined) return;
    if (offered.some((p) => p.id === draft.planId)) return;
    const next = offered.find((p) => p.code === PILOT_PLAN_CODE) ?? offered[0];
    update({ planId: next?.id ?? "" });
  }, [plans.data, offered, draft.planId]);
  const chosenPlan = offered.find((p) => p.id === draft.planId);

  // ---- the live subdomain check ------------------------------------------
  const subdomain = normalizeSubdomain(draft.subdomain);
  const shapeOk = SUBDOMAIN_PATTERN.test(subdomain);
  const debounced = useDebouncedValue(subdomain, 350);
  const check = useQuery({
    queryKey: ["platform", "subdomain-check", debounced],
    queryFn: ({ signal }) => checkSubdomain(debounced, signal),
    enabled: SUBDOMAIN_PATTERN.test(debounced),
    staleTime: 10_000,
    retry: false,
  });
  useSignInAgainOn401(check.error);
  const subdomainState: SubdomainState =
    subdomain === ""
      ? { kind: "empty" }
      : !shapeOk
        ? { kind: "invalid" }
        : debounced !== subdomain || check.isPending
          ? { kind: "checking" }
          : check.isError
            ? { kind: "unknown" }
            : check.data.available
              ? { kind: "available" }
              : check.data.reason === "reserved"
                ? { kind: "reserved" }
                : check.data.reason === "taken"
                  ? { kind: "taken" }
                  : { kind: "invalid" };

  const consoleUrl = new URL(window.location.href);
  const hostFor = (sub: string) => new URL(tenantOrigin(sub, consoleUrl)).host;

  // ---- step gates ---------------------------------------------------------
  const nameOk = draft.name.trim().length > 0 && draft.name.trim().length <= 200;
  // "unknown" lets the operator carry on: the create still decides.
  const step1Ok =
    nameOk && (subdomainState.kind === "available" || subdomainState.kind === "unknown");
  const step2Ok = draft.pack !== "" && chosenPlan !== undefined;
  const currencyOk = currencies.includes(draft.currency);
  const step3Ok = currencyOk && draft.timezone !== "";
  const emailOk = EMAIL_PATTERN.test(draft.adminEmail.trim());
  const stepOk = [step1Ok, step2Ok, step3Ok, emailOk][step - 1] ?? false;

  const create = useMutation({
    mutationFn: async (): Promise<TenantDetail> => {
      const body: CreateTenantInput = {
        name: draft.name.trim(),
        subdomain,
        vertical_pack: draft.pack,
        plan_id: draft.planId,
        region: draft.region,
        default_locale: draft.locale,
        currency: draft.currency,
        default_timezone: draft.timezone,
        group_admin_email: draft.adminEmail.trim(),
      };
      try {
        return await withHeldKey(heldKey, body, (key) => createTenant(body, key));
      } catch (error) {
        if (!isDefiniteAnswer(error)) {
          unclear.current.push(body);
          throw error;
        }
        const mine =
          error instanceof PlatformError && error.code === "SUBDOMAIN_TAKEN"
            ? await findOwnCreate(body.subdomain, unclear.current)
            : null;
        if (mine === null) throw error;
        return mine;
      }
    },
    onSuccess: (detail) => {
      setSubmitError(null);
      queryClient.setQueryData(tenantQueryKey(detail.tenant.id), detail);
      void queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });
      setCreated(detail);
    },
    onError: (error: unknown) => {
      if (error instanceof PlatformError && error.code === "SUBDOMAIN_TAKEN") {
        setSubmitError(t("admin.create.takenOnSubmit"));
        void queryClient.invalidateQueries({ queryKey: ["platform", "subdomain-check"] });
        setStep(1);
        return;
      }
      if (
        error instanceof PlatformError &&
        (error.code === "PACK_NOT_IN_PLAN" ||
          error.code === "PLAN_RETIRED" ||
          error.code === "PLAN_SANDBOX_ONLY" ||
          error.code === "PLAN_UNKNOWN")
      ) {
        setSubmitError(t("admin.createPlan.refused"));
        void queryClient.invalidateQueries({ queryKey: PLANS_QUERY });
        setStep(2);
        return;
      }
      if (error instanceof PlatformError && error.code === "SUBDOMAIN_RESERVED") {
        setSubmitError(t("admin.create.reservedOnSubmit"));
        setStep(1);
        return;
      }
      setSubmitError(
        !isDefiniteAnswer(error)
          ? t("admin.create.noAnswer")
          : actionFailure(error) === "role"
            ? t("admin.create.refusedRole")
            : t("admin.create.failed"),
      );
    },
  });
  useSignInAgainOn401(create.error);

  if (created !== null) {
    return <CreateProgress created={created} hostFor={hostFor} />;
  }

  const stepNames = [
    t("admin.create.stepIdentity"),
    t("admin.create.stepPack"),
    t("admin.create.stepDefaults"),
    t("admin.create.stepAdmin"),
  ];
  const stepper: StepperStep[] = stepNames.map((label, i) => ({
    key: String(i + 1),
    label,
    state: i + 1 < step ? "done" : i + 1 === step ? "current" : "todo",
    "data-testid": `admin-create-step-${String(i + 1)}`,
  }));

  return (
    <section className="admin-screen" data-testid="admin-tenant-create">
      <PageHeader
        title={t("admin.create.title")}
        crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        meta={t("admin.create.stepLabel", { n: step, total: STEP_COUNT })}
      />
      <div className="admin-wizard">
        <Card className="admin-wizard-steps">
          <Stepper steps={stepper} data-testid="admin-create-stepper" />
        </Card>
        <Card title={stepNames[step - 1]} className="admin-wizard-body">
          <form
            className="admin-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!stepOk) return;
              if (step < STEP_COUNT) {
                setStep(step + 1);
                return;
              }
              create.mutate();
            }}
          >
            {submitError !== null ? (
              <Banner kind="bad" data-testid="admin-create-error">
                {submitError}
                {create.isError && requestIdOf(create.error) !== undefined ? (
                  <span className="admin-reqid">
                    {" "}
                    {t("admin.load.requestId")}: <code>{requestIdOf(create.error)}</code>
                  </span>
                ) : null}
              </Banner>
            ) : null}

            {step === 1 ? (
              <>
                <Field label={t("admin.create.name")} wide>
                  <input
                    className="ds-input"
                    value={draft.name}
                    maxLength={200}
                    autoFocus
                    placeholder={t("admin.create.namePlaceholder")}
                    data-testid="admin-create-name"
                    onChange={(e) => {
                      const name = e.target.value;
                      update(
                        draft.subdomainEdited
                          ? { name }
                          : { name, subdomain: suggestSubdomain(name) },
                      );
                    }}
                  />
                </Field>
                <Field label={t("admin.create.subdomain")} wide>
                  <input
                    className="ds-input"
                    value={draft.subdomain}
                    maxLength={63}
                    spellCheck={false}
                    autoCapitalize="none"
                    data-testid="admin-create-subdomain"
                    aria-describedby="admin-create-subdomain-state"
                    onChange={(e) => {
                      update({ subdomain: e.target.value, subdomainEdited: true });
                    }}
                  />
                </Field>
                <p
                  id="admin-create-subdomain-state"
                  className={`admin-hint admin-sub-${subdomainState.kind}`}
                  data-testid="admin-create-subdomain-state"
                  data-state={subdomainState.kind}
                  aria-live="polite"
                >
                  {subdomainMessage(subdomainState, subdomain, hostFor, t)}
                </p>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <fieldset className="admin-packs" data-testid="admin-create-packs">
                  <legend className="ds-field-label filter-chip-label">
                    {t("admin.create.pack")}
                  </legend>
                  {packs.isPending ? (
                    <CardSkeleton lines={2} label={t("admin.create.packsLoading")} />
                  ) : packs.isError ? (
                    <ErrorState
                      title={t("admin.create.packsFailed")}
                      retryLabel={t("admin.load.retry")}
                      onRetry={() => {
                        void packs.refetch();
                      }}
                      requestId={requestIdOf(packs.error)}
                      requestIdLabel={t("admin.load.requestId")}
                    />
                  ) : (
                    packs.data.map((pack) => (
                      <label
                        key={pack.key}
                        className={
                          draft.pack === pack.key ? "admin-pack is-selected" : "admin-pack"
                        }
                        data-testid={`admin-create-pack-${pack.key}`}
                      >
                        <input
                          type="radio"
                          name="pack"
                          value={pack.key}
                          checked={draft.pack === pack.key}
                          onChange={() => {
                            update({ pack: pack.key });
                          }}
                        />
                        <span className="admin-pack-name">
                          {pickLocalized(pack.name, i18n.language) ?? pack.key}
                        </span>
                        <span className="admin-pack-desc">
                          {pickLocalized(pack.description, i18n.language) ?? ""}
                        </span>
                      </label>
                    ))
                  )}
                </fieldset>
                <Field label={t("admin.createPlan.label")} wide>
                  {plans.isPending ? (
                    <CardSkeleton lines={1} label={t("admin.createPlan.loading")} />
                  ) : plans.isError ? (
                    <ErrorState
                      title={t("admin.createPlan.failed")}
                      retryLabel={t("admin.load.retry")}
                      onRetry={() => {
                        void plans.refetch();
                      }}
                      requestId={requestIdOf(plans.error)}
                      requestIdLabel={t("admin.load.requestId")}
                    />
                  ) : offered.length === 0 ? (
                    <p className="admin-hint" data-testid="admin-create-no-plan">
                      {t("admin.createPlan.none")}
                    </p>
                  ) : (
                    <Select
                      value={draft.planId}
                      data-testid="admin-create-plan"
                      onChange={(e) => {
                        update({ planId: e.target.value });
                      }}
                    >
                      {offered.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.annual_price_minor === null
                            ? p.name
                            : `${p.name} · ${formatMinor(p.annual_price_minor, p.currency, i18n.language)}`}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t("admin.create.region")}>
                  <Select
                    value={draft.region}
                    data-testid="admin-create-region"
                    onChange={(e) => {
                      update({ region: e.target.value as PlatformRegion });
                    }}
                  >
                    {PLATFORM_REGIONS.map((r) => (
                      <option key={r} value={r}>
                        {t(`admin.regions.${r}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <Field label={t("admin.create.locale")}>
                  <Select
                    value={draft.locale}
                    data-testid="admin-create-locale"
                    onChange={(e) => {
                      update({ locale: e.target.value as TenantLocale });
                    }}
                  >
                    {TENANT_LOCALES.map((l) => (
                      <option key={l} value={l}>
                        {LOCALE_ENDONYMS[l]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t("admin.create.currency")}>
                  <Select
                    value={draft.currency}
                    data-testid="admin-create-currency"
                    onChange={(e) => {
                      update({ currency: e.target.value });
                    }}
                  >
                    {currencies.map((c) => (
                      <option key={c} value={c}>
                        {currencyLabel(c, i18n.language)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <p className="admin-hint">{t("admin.create.currencyHint")}</p>
                <Field label={t("admin.create.timezone")} wide>
                  <Select
                    value={draft.timezone}
                    data-testid="admin-create-timezone"
                    onChange={(e) => {
                      update({ timezone: e.target.value });
                    }}
                  >
                    {(zones.includes(draft.timezone) ? zones : [draft.timezone, ...zones]).map(
                      (z) => (
                        <option key={z} value={z}>
                          {z}
                        </option>
                      ),
                    )}
                  </Select>
                </Field>
              </>
            ) : null}

            {step === 4 ? (
              <>
                <Field label={t("admin.create.adminEmail")} wide>
                  <input
                    className="ds-input"
                    type="email"
                    value={draft.adminEmail}
                    autoFocus
                    spellCheck={false}
                    data-testid="admin-create-admin-email"
                    onChange={(e) => {
                      update({ adminEmail: e.target.value });
                    }}
                  />
                </Field>
                <p className="admin-hint">
                  {draft.adminEmail.trim() === "" || emailOk
                    ? t("admin.create.adminEmailHint")
                    : t("admin.create.emailInvalid")}
                </p>
                <h3 className="admin-section-t">{t("admin.create.review")}</h3>
                <Facts data-testid="admin-create-review">
                  <Fact label={t("admin.create.name")}>{draft.name.trim()}</Fact>
                  <Fact label={t("admin.tenant.subdomain")}>{hostFor(subdomain)}</Fact>
                  <Fact label={t("admin.create.pack")}>
                    {packLabel(draft.pack, packs.data, i18n.language)}
                  </Fact>
                  <Fact label={t("admin.createPlan.label")}>{chosenPlan?.name ?? ""}</Fact>
                  <Fact label={t("admin.create.region")}>{t(`admin.regions.${draft.region}`)}</Fact>
                  <Fact label={t("admin.create.locale")}>{LOCALE_ENDONYMS[draft.locale]}</Fact>
                  <Fact label={t("admin.create.currency")}>{draft.currency}</Fact>
                  <Fact label={t("admin.create.timezone")}>{draft.timezone}</Fact>
                </Facts>
              </>
            ) : null}

            <div className="admin-actions admin-wizard-actions">
              {step > 1 ? (
                <Button
                  disabled={create.isPending}
                  data-testid="admin-create-back"
                  onClick={() => {
                    setStep(step - 1);
                  }}
                >
                  {t("admin.create.back")}
                </Button>
              ) : (
                <Button
                  data-testid="admin-create-cancel"
                  onClick={() => {
                    void navigate({ to: "/console/tenants" });
                  }}
                >
                  {t("admin.create.cancel")}
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={!stepOk}
                busy={create.isPending}
                busyLabel={t("admin.create.creating")}
                data-testid={step < STEP_COUNT ? "admin-create-next" : "admin-create-submit"}
              >
                {step < STEP_COUNT ? t("admin.create.next") : t("admin.create.submit")}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
}

/** "KZT · Kazakhstani Tenge", in the viewer's language where the browser can name it. */
function currencyLabel(code: string, language: string): string {
  try {
    const name = new Intl.DisplayNames([language], { type: "currency" }).of(code);
    return name !== undefined && name !== code ? `${code} · ${name}` : code;
  } catch {
    return code;
  }
}

/**
 * The tenant an earlier, unanswered create made at this subdomain, if the one
 * there now matches what that create sent. Null when there is none, or when the
 * lookup itself fails (the "taken" message is then the honest answer).
 */
async function findOwnCreate(
  subdomain: string,
  attempts: readonly CreateAttempt[],
): Promise<TenantDetail | null> {
  if (attempts.length === 0) return null;
  try {
    const page = await listTenants({ q: subdomain, limit: 25 });
    const found = page.items.find((tenant) => isOwnCreate(tenant, attempts));
    return found === undefined ? null : await getTenant(found.id);
  } catch {
    return null;
  }
}

function subdomainMessage(
  state: SubdomainState,
  subdomain: string,
  hostFor: (sub: string) => string,
  t: TFunction,
): string {
  switch (state.kind) {
    case "empty":
      return t("admin.create.subdomainHint");
    case "invalid":
      return t("admin.create.invalid");
    case "checking":
      return t("admin.create.checking");
    case "available":
      return t("admin.create.available", { host: hostFor(subdomain) });
    case "reserved":
      return t("admin.create.reserved");
    case "taken":
      return t("admin.create.taken");
    case "unknown":
      return t("admin.create.checkFailed");
  }
}

/** After Create: follow the tenant through setup until it is live or stops. */
function CreateProgress({
  created,
  hostFor,
}: {
  created: TenantDetail;
  hostFor: (sub: string) => string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = created.tenant.id;
  const detail = useQuery({
    queryKey: tenantQueryKey(id),
    queryFn: ({ signal }) => getTenant(id, signal),
    initialData: created,
    refetchInterval: (query) => {
      const status = query.state.data?.tenant.status;
      return status !== undefined && !isProvisioningSettled(status) ? TENANT_POLL_MS : false;
    },
  });
  useSignInAgainOn401(detail.error);
  const retryKey = useHeldKey();
  const retry = useMutation({
    mutationFn: () => withHeldKey(retryKey, { retry: id }, (key) => retryProvisioning(id, key)),
    onSuccess: (next) => {
      queryClient.setQueryData(tenantQueryKey(id), next);
    },
    onError: (error: unknown) => {
      if (actionFailure(error) === "changed") {
        void queryClient.invalidateQueries({ queryKey: tenantQueryKey(id) });
      }
    },
  });
  useSignInAgainOn401(retry.error);

  const view = detail.data;
  const status = view.tenant.status;
  const host = hostFor(view.tenant.subdomain);
  const name = view.tenant.name;

  return (
    <section className="admin-screen" data-testid="admin-create-progress" data-status={status}>
      <PageHeader
        title={
          status === "active"
            ? t("admin.create.progressLive", { name })
            : status === "provision_failed"
              ? t("admin.create.progressFailed")
              : t("admin.create.progressTitle", { name })
        }
        crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
      />
      <Card className="admin-progress-card">
        <ProvisioningSteps detail={view} />
        {status === "provision_failed" && view.provisioning?.last_error ? (
          <Banner kind="bad" data-testid="admin-create-progress-error">
            <code className="admin-wrap">{view.provisioning.last_error}</code>
          </Banner>
        ) : null}
        {detail.isError ? (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
            }}
            requestId={requestIdOf(detail.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        ) : null}
        <div className="admin-actions">
          {status === "active" ? (
            <a
              className="admin-live-link"
              href={tenantOrigin(view.tenant.subdomain, new URL(window.location.href))}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-create-open"
            >
              {t("admin.create.openTenant", { host })}
            </a>
          ) : null}
          {status === "provision_failed" ? (
            <Button
              variant="primary"
              busy={retry.isPending}
              busyLabel={t("admin.tenant.retrying")}
              data-testid="admin-create-retry"
              onClick={() => {
                retry.mutate();
              }}
            >
              {t("admin.tenant.retry")}
            </Button>
          ) : null}
          <Button
            data-testid="admin-create-view"
            onClick={() => {
              void navigate({ to: "/console/tenants/$tenantId", params: { tenantId: id } });
            }}
          >
            {t("admin.create.viewTenant")}
          </Button>
        </div>
        {retry.isError ? (
          <Banner kind="bad" data-testid="admin-create-retry-error">
            {actionMessage(retry.error, t("admin.tenant.retryFailed"), t)}
          </Banner>
        ) : null}
      </Card>
    </section>
  );
}
